import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { GlobalState } from "store/configure-store";
import {
  MedicationEntryPrescriptionStatusEnum,
  MedicationEntryType,
} from "model/medication-entry.model";
import { RouteComponentProps, withRouter } from "react-router";
import { TemporalityEnum } from "common/enums/TemporalityEnum";
import { PosologyTypeEnum } from "common/enums/PosologyTypeEnum";
import { selectActiveMedicationEntry } from "store/reducers/medication.reducer";
import {
  editActiveMedication,
  newMedication,
  removeMedication,
  resetActiveMedication,
} from "store/actions/medication.actions";
import { usePrevious } from "common/hooks/previous.hook";
import { PeriodEnum } from "common/enums/PeriodEnum";
import { StatusTypeEnum } from "common/enums/StatusTypeEnum";
import { Icon, Modal, Navbar, Page } from "@cc/react-components";
import MedicationRoutes from "main/MedicationFlow/medication-routes";

interface Props extends React.HTMLAttributes<HTMLElement>, RouteComponentProps {
  isModalOpen: boolean;
  setModalOpen: (isModalOpen: boolean) => void;
  baseLocation: string;
}

const MedicationFlow = ({
  isModalOpen,
  setModalOpen,
  activeMedicationEntry,
  baseLocation,
  history,
  editActiveMedication,
  resetActiveMedication,
  removeMedication,
}: Props & PropsFromDispatch & PropsFromState) => {
  const [t] = useTranslation();

  /** Local Flow State */
  const [fromMedicationSearch, setFromMedicationSearch] = useState(false);
  const [selectedTemporality, setSelectedTemporality] = useState<
    TemporalityEnum
  >();
  const [selectedStatus, setSelectedStatus] = useState<StatusTypeEnum>();
  const [selectedPosologyType, setSelectedPosologyType] = useState<
    PosologyTypeEnum
  >(PosologyTypeEnum.FREE_TEXT);
  const [freePosology, setFreePosology] = useState<string>("");
  const [selectionPeriod, setSelectionPeriod] = useState<{
    number: number;
    unit: PeriodEnum;
  }>({ number: 1, unit: PeriodEnum.DAYS });

  /** Update Data on new editMedicationEntry */
  const updateLocalState = (medicationEntry: MedicationEntryType) => {
    setSelectedTemporality(medicationEntry.temporality);
    setSelectedPosologyType(PosologyTypeEnum.FREE_TEXT);
    setSelectedStatus(medicationEntry.status);
    setFreePosology(medicationEntry.posology.posologyText);
    setSelectionPeriod({
      number: medicationEntry.posology.periodNumber || 1,
      unit: medicationEntry.posology.periodUnit || PeriodEnum.DAYS,
    });
  };

  const prevActiveMedicationEntry = usePrevious(activeMedicationEntry);
  useEffect(() => {
    !!activeMedicationEntry && updateLocalState(activeMedicationEntry);
    !!prevActiveMedicationEntry &&
      !activeMedicationEntry &&
      resetLocalState() &&
      history.replace(baseLocation);
  }, [activeMedicationEntry, history, baseLocation, prevActiveMedicationEntry]);

  const isVOS = (title: string): boolean => {
    const first = title.charAt(0);
    return first === first.toLowerCase();
  };

  /** When medication is edited */
  const onEdit = () => {
    if (activeMedicationEntry && activeMedicationEntry.id !== undefined) {
      let updatedMedicationEntry;
      if (selectedStatus === StatusTypeEnum.NOT_RELEVANT) {
        updatedMedicationEntry = Object.assign({}, activeMedicationEntry, {
          status: selectedStatus,
        });
      } else {
        updatedMedicationEntry = Object.assign({}, activeMedicationEntry, {
          temporality: selectedTemporality,
          status: selectedStatus,
          prescriptionStatus:
            activeMedicationEntry.prescriptionStatus ===
            MedicationEntryPrescriptionStatusEnum.NEW
              ? MedicationEntryPrescriptionStatusEnum.NEW
              : MedicationEntryPrescriptionStatusEnum.EDITED,
          posology: isVOS(activeMedicationEntry.freeTextContent)
            ? {
                type: selectedPosologyType,
                posologyText: freePosology,
                periodNumber: selectionPeriod.number,
                periodUnit: selectionPeriod.unit,
              }
            : {
                type: selectedPosologyType,
                posologyText: freePosology,
              },
        });
      }

      editActiveMedication(updatedMedicationEntry);
      setModalOpen(false);
      history.replace(baseLocation);
    }
  };

  const resetLocalState = () => {
    setFromMedicationSearch(false);
    setSelectionPeriod({ number: 1, unit: PeriodEnum.DAYS });
    setSelectedTemporality(TemporalityEnum.ACUTE);
    setSelectedPosologyType(PosologyTypeEnum.FREE_TEXT);
    setFreePosology("");
  };

  const closeFlow = () => {
    resetLocalState();
    resetActiveMedication();
    fromMedicationSearch &&
      activeMedicationEntry &&
      removeMedication(activeMedicationEntry);
    setModalOpen(false);
  };

  return (
    <Modal
      show={isModalOpen}
      onShow={() => setModalOpen(true)}
      onHide={() => setModalOpen(false)}
      size="fullscreen"
    >
      <>
        <Navbar>
          <div
            className="w-100 h-11 d-flex align-items-center"
            onClick={closeFlow}
          >
            <Icon type="arrow-left" className="text-white pr-2" size="lg" />
            <h2 className="m-0 font-weight-bold text-white">
              {!!activeMedicationEntry
                ? activeMedicationEntry.freeTextContent
                : t("medicationFlow.new")}
            </h2>
          </div>
        </Navbar>
        <Page className="p-0 bg-light">
          <MedicationRoutes
            activeMedicationEntry={activeMedicationEntry}
            setFromMedicationSearch={setFromMedicationSearch}
            selectedTemporality={selectedTemporality}
            setSelectedTemporality={setSelectedTemporality}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedPosologyType={selectedPosologyType}
            setSelectedPosologyType={setSelectedPosologyType}
            freePosology={freePosology}
            setFreePosology={setFreePosology}
            selectionPeriod={selectionPeriod}
            setSelectionPeriod={setSelectionPeriod}
            isVOS={isVOS}
            onEdit={onEdit}
          />
        </Page>
      </>
    </Modal>
  );
};

interface PropsFromDispatch {
  editActiveMedication: (medicationEntry: MedicationEntryType) => void;
  resetActiveMedication: () => void;
  removeMedication: (medicationEntry: MedicationEntryType) => void;
}

interface PropsFromState {
  activeMedicationEntry?: MedicationEntryType;
}

const mapStateToProps = (state: GlobalState) => {
  return {
    activeMedicationEntry: selectActiveMedicationEntry(state.medicationContext),
  };
};

export default withRouter(
  connect(mapStateToProps, {
    editActiveMedication,
    resetActiveMedication,
    newMedication,
    removeMedication,
  })(MedicationFlow)
);
